/**
 * Share-threshold rules for the shareholder graph filter.
 *
 * Moved from filterLinksByPercentage (shareholderGraphUtils.js) and applied per share instead of
 * per link, because parallel relationship rows between the same owner and owned company are
 * merged into one edge that carries several shares.
 *
 * Rules (unchanged, plus the '≥' prefix):
 * - '25' / 25 kept when 25 >= filter
 * - '>50' kept when 50 >= filter
 * - '≥25' kept when 25 >= filter (new; the BE formats bounded shares this way)
 * - '<5' kept when 5 > filter
 * - 'GP' always kept, even when unknown values are excluded
 * - anything else (null, text) follows includeUnknownShares
 * - a filter that is not a number keeps everything
 * - subsidiary edges follow only the includeSubsidiaries toggle
 */
import { GENERAL_PARTNER_SHARE } from './graphConstants';

const PREFIX_OPERATORS = ['≥', '>', '<'];

const COMPARATORS = {
  '=': (value, filter) => value >= filter,
  '≥': (value, filter) => value >= filter,
  '>': (value, filter) => value >= filter,
  '<': (value, filter) => value > filter
};

const parsePrefixedValue = (text) => {
  const match = text.match(/[\d.]+/);
  return match ? Number.parseFloat(match[0]) : Number.NaN;
};

/**
 * Parses a raw share value.
 * @param {string|number|null|undefined} share - e.g. '25', '33.33', '>50', '≥25', '<5', 'GP', null
 * @returns {{op: '='|'>'|'<'|'≥', value: number}|'GP'|null} null when there is no usable number
 */
export const parseShareThreshold = (share) => {
  if (share == null) {
    return null;
  }
  if (typeof share === 'number') {
    return Number.isFinite(share) ? { op: '=', value: share } : null;
  }
  const text = String(share).trim();
  if (text === GENERAL_PARTNER_SHARE) {
    return GENERAL_PARTNER_SHARE;
  }
  const op = PREFIX_OPERATORS.find((prefix) => text.startsWith(prefix));
  const value = op ? parsePrefixedValue(text) : Number.parseFloat(text);
  if (Number.isNaN(value)) {
    return null;
  }
  return { op: op ?? '=', value };
};

/** True when the share carries a number (plain or bounded), false for GP and unknown values. */
export const isNumericShare = (share) => {
  const parsed = parseShareThreshold(share);
  return parsed !== null && parsed !== GENERAL_PARTNER_SHARE;
};

/**
 * The number a share stands for when ordering by size: its value, for a bound too ('>50' is 50,
 * '<5' is 5); null for 'GP' and unknown values.
 * @param {string|number|null} share
 * @returns {number|null}
 */
export const shareValue = (share) => {
  const parsed = parseShareThreshold(share);
  return parsed && parsed !== GENERAL_PARTNER_SHARE ? parsed.value : null;
};

const isLowerBoundPart = (part) => !part || part.op === '>' || part.op === '≥';

/**
 * Adds shares up to a total that claims no more than its parts do:
 * - every part a number: the sum ('4' and '5' give =9)
 * - a part with a lower bound ('>', '≥') or of unknown size: a lower bound, '<' and unknown parts
 *   counting as 0 ('>50' and '4.5' give >54.5, null and '4.5' give ≥4.5)
 * - otherwise a part below a bound makes the sum an upper bound ('<5' and '4.5' give <9.5)
 * @param {Array<string|number|null>} shares
 * @returns {{op: '='|'>'|'≥'|'<', value: number}|null} null when nothing is known
 */
export const sumShareBounds = (shares) => {
  const parts = shares.map(parseShareThreshold).map((part) => (part?.op ? part : null));
  const known = parts.filter(Boolean);
  if (!known.length) return null;
  if (parts.some(isLowerBoundPart)) {
    const value = known
      .filter((part) => part.op !== '<')
      .reduce((sum, part) => sum + part.value, 0);
    if (value <= 0) return null;
    return { op: known.some((part) => part.op === '>') ? '>' : '≥', value };
  }
  const value = known.reduce((sum, part) => sum + part.value, 0);
  return { op: known.some((part) => part.op === '<') ? '<' : '=', value };
};

/**
 * Whether one share passes the percentage filter.
 * @param {string|number|null} share
 * @param {{percentageFilter: number, includeUnknownShares: boolean}} filters
 * @returns {boolean}
 */
export const passesShareFilter = (share, filters) => {
  // parseFloat, not Number(): null and '' mean "no filter" exactly as they did before.
  const percentageFilter = Number.parseFloat(filters?.percentageFilter);
  if (Number.isNaN(percentageFilter)) {
    return true;
  }
  const parsed = parseShareThreshold(share);
  if (parsed === GENERAL_PARTNER_SHARE) {
    return true;
  }
  if (parsed === null) {
    return !!filters.includeUnknownShares;
  }
  return COMPARATORS[parsed.op](parsed.value, percentageFilter);
};

/**
 * The shares of a merged edge that survive the filter. An empty result drops the edge.
 * @param {{shares: Array, isSubsidiary: boolean}} edge
 * @param {{percentageFilter: number, includeUnknownShares: boolean, includeSubsidiaries: boolean}} filters
 * @returns {Array} kept raw shares, in input order
 */
export const filterEdgeShares = (edge, filters) => {
  if (edge.isSubsidiary) {
    return filters?.includeSubsidiaries ? [...edge.shares] : [];
  }
  return edge.shares.filter((share) => passesShareFilter(share, filters));
};
